import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API_BASE, UGX } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, SectionCard, KpiCard, EmptyState, StatusPill, Chip } from "../components/Common";
import { Radio, Upload, Play, FileText, User, Clock, AlertTriangle, CheckCircle2, X, Download, Plus } from "lucide-react";

const STATUS_COLORS = {
  "Requested":   "amber",
  "In Progress": "blue",
  "Reported":    "green",
};

export default function Imaging() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("queue"); // queue | inprogress | reported
  const [urgentOnly, setUrgentOnly] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const isRadiologist = user?.role === "radiologist" || user?.role === "hospital_admin" || user?.role === "super_admin";
  const isDoctor = user?.role === "doctor";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/imaging/orders");
      setOrders(data || []);
    } catch (e) { toast.error("Failed to load imaging orders"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = orders.filter((o) => {
    // Urgent overlay: show any open (Requested + In Progress) order flagged STAT/Urgent, regardless of tab
    if (urgentOnly) return o.status !== "Reported" && (o.priority === "STAT" || o.priority === "Urgent");
    if (tab === "queue") return o.status === "Requested";
    if (tab === "inprogress") return o.status === "In Progress";
    if (tab === "reported") return o.status === "Reported";
    return true;
  });

  const counts = {
    queue: orders.filter((o) => o.status === "Requested").length,
    inprogress: orders.filter((o) => o.status === "In Progress").length,
    reported: orders.filter((o) => o.status === "Reported").length,
    urgent: orders.filter((o) => o.status !== "Reported" && (o.priority === "STAT" || o.priority === "Urgent")).length,
  };

  const switchTab = (k) => { setUrgentOnly(false); setTab(k); };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="imaging-page">
      <PageHeader
        title="Radiology & Imaging"
        subtitle={isRadiologist ? "Fulfil imaging requests, upload PDF reports and notify ordering clinicians." : "Track imaging you have ordered and download completed reports."}
        actions={isDoctor ? (
          <button data-testid="doc-order-imaging-btn" onClick={() => setShowOrderModal(true)} className="h-10 px-4 rounded-lg bg-[#1976D2] text-white text-sm font-medium flex items-center gap-2 hover:bg-[#1565C0]">
            <Plus className="w-4 h-4" /> Order imaging
          </button>
        ) : null}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="img-kpi-queue"      onClick={() => { setUrgentOnly(false); setTab("queue"); }}      label="Awaiting radiology" value={counts.queue}      tone="amber" icon={Clock}          hint="Requested — not started" />
        <KpiCard testId="img-kpi-inprogress" onClick={() => { setUrgentOnly(false); setTab("inprogress"); }} label="In progress"        value={counts.inprogress} tone="blue"  icon={Play}           hint="Being performed" />
        <KpiCard testId="img-kpi-reported"   onClick={() => { setUrgentOnly(false); setTab("reported"); }}   label="Reports ready"      value={counts.reported}   tone="green" icon={CheckCircle2}   hint="Reported to clinician" />
        <KpiCard testId="img-kpi-urgent"     onClick={() => setUrgentOnly(true)}                             label="Urgent / STAT open" value={counts.urgent}     tone="red"   icon={AlertTriangle}  hint="Across all open orders" />
      </div>

      {urgentOnly && (
        <div className="rounded-lg border border-[#B71C1C]/25 bg-[#fdf1f1] px-4 py-2.5 flex items-center justify-between" data-testid="img-filter-banner">
          <div className="text-sm text-[#7a1414]">
            Showing <span className="font-semibold">Urgent / STAT open orders · {counts.urgent}</span> across Requested + In Progress
          </div>
          <button data-testid="img-clear-filter" onClick={() => setUrgentOnly(false)} className="h-8 px-3 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-xs font-medium flex items-center gap-1">
            <X className="w-3 h-3" /> Show all
          </button>
        </div>
      )}

      <div className={`border-b border-slate-200 flex gap-1 ${urgentOnly ? "opacity-40 pointer-events-none" : ""}`}>
        {[
          ["queue", `Requested (${counts.queue})`],
          ["inprogress", `In Progress (${counts.inprogress})`],
          ["reported", `Reported (${counts.reported})`],
        ].map(([k, label]) => (
          <button key={k} data-testid={`imaging-tab-${k}`} onClick={() => switchTab(k)} className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === k ? "border-[#1976D2] text-[#1565C0]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{label}</button>
        ))}
      </div>

      <SectionCard>
        {loading ? (
          <div className="p-10 text-center text-slate-500 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState icon={Radio} title="Nothing here yet" description={tab === "queue" ? "New imaging requests will appear here." : tab === "inprogress" ? "Studies picked up by radiology will appear here." : "Completed reports will appear here once uploaded."} />
        ) : (
          <table className="w-full text-sm" data-testid="imaging-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Order</th>
                <th className="text-left px-4 py-3">Patient</th>
                <th className="text-left px-4 py-3">Study</th>
                <th className="text-left px-4 py-3">Priority</th>
                <th className="text-right px-4 py-3">Price</th>
                <th className="text-left px-4 py-3">Ordered</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((o) => (
                <tr key={o.order_id} data-testid={`imaging-row-${o.order_id}`} className="hover:bg-slate-50">
                  <td className="px-4 py-3 tabular text-xs text-slate-500">{o.order_no}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => navigate(`/app/patients/${o.patient_id}`)} className="text-left hover:underline">
                      <div className="font-medium text-slate-800">{o.patient_name}</div>
                      <div className="text-xs text-slate-500 tabular">{o.patient_ths_id}</div>
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{o.imaging_type}</div>
                    <div className="text-xs text-slate-500">{o.body_part || "—"}</div>
                    <div className="text-xs text-slate-500 mt-0.5 line-clamp-1 max-w-xs">{o.clinical_indication}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Chip tone={o.priority === "STAT" ? "red" : o.priority === "Urgent" ? "amber" : "slate"}>{o.priority}</Chip>
                  </td>
                  <td className="px-4 py-3 text-right tabular">{UGX(o.price)}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    <div>{o.ordered_by_name || "—"}</div>
                    <div className="tabular">{new Date(o.created_at).toLocaleString("en-UG", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}</div>
                  </td>
                  <td className="px-4 py-3"><StatusPill status={o.status === "Reported" ? "Completed" : o.status === "In Progress" ? "In Progress" : "Pending"} /></td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button data-testid={`view-imaging-${o.order_id}`} onClick={() => setSelected(o)} className="text-xs text-[#1565C0] font-medium hover:underline">Open</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>

      {selected && <DetailModal order={selected} onClose={() => setSelected(null)} onDone={() => { setSelected(null); load(); }} isRadiologist={isRadiologist} />}
      {showOrderModal && <NewOrderModal onClose={() => setShowOrderModal(false)} onDone={() => { setShowOrderModal(false); load(); }} />}
    </div>
  );
}

function DetailModal({ order, onClose, onDone, isRadiologist }) {
  const [findings, setFindings] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const start = async () => {
    setBusy(true);
    try {
      await api.post(`/imaging/orders/${order.order_id}/start`);
      toast.success("Study marked as In Progress");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); }
    finally { setBusy(false); }
  };

  const upload = async () => {
    if (!file) { toast.error("Choose a PDF file first"); return; }
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF files are accepted");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (findings) fd.append("findings", findings);
      const t = localStorage.getItem("ths_session_token");
      const url = `${API_BASE}/imaging/orders/${order.order_id}/report${findings ? `?findings=${encodeURIComponent(findings)}` : ""}`;
      const res = await fetch(url, {
        method: "POST",
        body: fd,
        credentials: "include",
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (${res.status})`);
      }
      toast.success("Report uploaded — ordering clinician notified");
      onDone();
    } catch (e) { toast.error(e.message || "Upload failed"); }
    finally { setBusy(false); }
  };

  const download = async () => {
    try {
      const t = localStorage.getItem("ths_session_token");
      const res = await fetch(`${API_BASE}/imaging/orders/${order.order_id}/report`, {
        credentials: "include",
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) { toast.error(e.message || "Cannot open report"); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="imaging-detail-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-xs text-slate-500 tabular">{order.order_no}</div>
            <div className="text-lg font-semibold">{order.imaging_type}{order.body_part ? ` — ${order.body_part}` : ""}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm mb-4">
          <div>
            <div className="text-[11px] uppercase text-slate-500">Patient</div>
            <div className="font-medium">{order.patient_name}</div>
            <div className="text-xs text-slate-500 tabular">{order.patient_ths_id}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase text-slate-500">Priority</div>
            <div><Chip tone={order.priority === "STAT" ? "red" : order.priority === "Urgent" ? "amber" : "slate"}>{order.priority}</Chip></div>
          </div>
          <div>
            <div className="text-[11px] uppercase text-slate-500">Ordered by</div>
            <div>{order.ordered_by_name}</div>
            <div className="text-xs text-slate-500">{new Date(order.created_at).toLocaleString()}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase text-slate-500">Fee (auto-billed)</div>
            <div className="tabular font-semibold">{UGX(order.price)}</div>
          </div>
          <div className="col-span-2">
            <div className="text-[11px] uppercase text-slate-500">Clinical indication</div>
            <div className="text-sm mt-1">{order.clinical_indication}</div>
          </div>
          {order.notes && (
            <div className="col-span-2">
              <div className="text-[11px] uppercase text-slate-500">Notes</div>
              <div className="text-sm mt-1">{order.notes}</div>
            </div>
          )}
        </div>

        {order.status === "Reported" ? (
          <div className="rounded-lg border border-[#c9e5d1] bg-[#eef8f0] p-4">
            <div className="flex items-center gap-2 text-[#1B5E20] text-sm font-medium mb-2">
              <CheckCircle2 className="w-4 h-4" /> Report uploaded
            </div>
            <div className="text-xs text-slate-600 mb-1">By {order.report_uploaded_by_name} · {new Date(order.report_uploaded_at).toLocaleString()}</div>
            {order.findings && <div className="text-sm mt-2 whitespace-pre-wrap">{order.findings}</div>}
            <button data-testid="download-imaging-report" onClick={download} className="mt-3 h-9 px-3 rounded-lg bg-[#2E7D32] text-white text-sm font-medium flex items-center gap-2 hover:bg-[#256a2b]">
              <Download className="w-4 h-4" /> Open PDF report
            </button>
          </div>
        ) : isRadiologist ? (
          <div className="space-y-3">
            {order.status === "Requested" && (
              <button data-testid="imaging-start-btn" disabled={busy} onClick={start} className="w-full h-10 rounded-lg bg-[#1976D2] text-white text-sm font-semibold hover:bg-[#1565C0] disabled:opacity-50">
                Mark as In Progress
              </button>
            )}
            <div className="rounded-lg border border-slate-200 p-4">
              <div className="text-sm font-semibold mb-2">Upload PDF report</div>
              <label className="block">
                <span className="text-[11px] uppercase text-slate-500 font-semibold">Findings / Impression (optional)</span>
                <textarea data-testid="imaging-findings" value={findings} onChange={(e) => setFindings(e.target.value)} rows={4} placeholder="e.g. No focal consolidation. Cardiothoracic ratio within normal limits." className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40" />
              </label>
              <input ref={fileRef} type="file" accept="application/pdf,.pdf" data-testid="imaging-pdf-input" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-3 block w-full text-sm text-slate-600 file:mr-3 file:h-9 file:px-3 file:rounded-lg file:border-0 file:bg-[#e6f0fb] file:text-[#1565C0] file:font-medium hover:file:bg-[#d0e4fb]" />
              {file && <div className="mt-2 text-xs text-slate-500">Selected: {file.name} · {(file.size/1024).toFixed(0)} KB</div>}
              <button data-testid="imaging-upload-btn" onClick={upload} disabled={busy || !file} className="mt-3 w-full h-10 rounded-lg bg-[#2E7D32] text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[#256a2b] disabled:opacity-50">
                <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Upload report & notify doctor"}
              </button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-slate-500 italic">Report not yet available. You will be notified when radiology completes this study.</div>
        )}
      </div>
    </div>
  );
}

function NewOrderModal({ onClose, onDone, prefillPatient }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [patient, setPatient] = useState(prefillPatient || null);
  const [imagingType, setImagingType] = useState("X-Ray");
  const [bodyPart, setBodyPart] = useState("");
  const [indication, setIndication] = useState("");
  const [price, setPrice] = useState(45000);
  const [priority, setPriority] = useState("Routine");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const types = ["X-Ray", "CT", "MRI", "Ultrasound", "ECG", "Mammography"];

  useEffect(() => {
    let t = setTimeout(async () => {
      if (patient || q.length < 2) { setResults([]); return; }
      try {
        const { data } = await api.get(`/patients?q=${encodeURIComponent(q)}&limit=8`);
        setResults(data || []);
      } catch { setResults([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, patient]);

  const submit = async () => {
    if (!patient) { toast.error("Select a patient"); return; }
    if (!indication.trim()) { toast.error("Clinical indication is required"); return; }
    if (!(Number(price) > 0)) { toast.error("Enter a price greater than 0"); return; }
    setBusy(true);
    try {
      await api.post("/imaging/orders", {
        patient_id: patient.patient_id,
        imaging_type: imagingType,
        body_part: bodyPart || null,
        clinical_indication: indication,
        price: Number(price),
        priority,
        notes: notes || null,
      });
      toast.success(`${imagingType} requested — radiology has been notified. ${UGX(price)} auto-billed.`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create order"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="new-imaging-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="text-lg font-semibold">Order imaging study</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>

        {/* Patient picker */}
        <div className="mb-4">
          <div className="text-[11px] uppercase text-slate-500 font-semibold mb-1">Patient</div>
          {patient ? (
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
              <div>
                <div className="text-sm font-medium">{patient.first_name} {patient.last_name}</div>
                <div className="text-xs text-slate-500 tabular">{patient.ths_id} · {patient.phone_primary}</div>
              </div>
              {!prefillPatient && <button data-testid="imaging-change-patient" onClick={() => { setPatient(null); setQ(""); }} className="text-xs text-[#1565C0] hover:underline">Change</button>}
            </div>
          ) : (
            <>
              <input data-testid="imaging-patient-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, Patient ID or phone…" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40" />
              {results.length > 0 && (
                <div className="mt-1 border border-slate-200 rounded-lg overflow-hidden">
                  {results.map((p) => (
                    <button key={p.patient_id} data-testid={`select-patient-${p.patient_id}`} onClick={() => { setPatient(p); setResults([]); }} className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0">
                      <div className="text-sm font-medium">{p.first_name} {p.last_name}</div>
                      <div className="text-xs text-slate-500 tabular">{p.ths_id} · {p.phone_primary}</div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block col-span-1">
            <span className="text-[11px] uppercase text-slate-500 font-semibold">Imaging type *</span>
            <select data-testid="imaging-type-select" value={imagingType} onChange={(e) => setImagingType(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40">
              {types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="block col-span-1">
            <span className="text-[11px] uppercase text-slate-500 font-semibold">Body part / view</span>
            <input data-testid="imaging-body-part" value={bodyPart} onChange={(e) => setBodyPart(e.target.value)} placeholder="e.g. Chest PA, Abdominal" className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40" />
          </label>
          <label className="block col-span-2">
            <span className="text-[11px] uppercase text-slate-500 font-semibold">Clinical indication *</span>
            <textarea data-testid="imaging-indication" value={indication} onChange={(e) => setIndication(e.target.value)} rows={2} placeholder="Why is this study being ordered?" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40" />
          </label>
          <label className="block col-span-1">
            <span className="text-[11px] uppercase text-slate-500 font-semibold">Price (UGX) *</span>
            <input data-testid="imaging-price" type="number" min="1" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm tabular focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40" />
            <span className="text-[11px] text-slate-500 mt-1 block">Auto-billed to the patient invoice.</span>
          </label>
          <label className="block col-span-1">
            <span className="text-[11px] uppercase text-slate-500 font-semibold">Priority</span>
            <select data-testid="imaging-priority" value={priority} onChange={(e) => setPriority(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40">
              <option>Routine</option><option>Urgent</option><option>STAT</option>
            </select>
          </label>
          <label className="block col-span-2">
            <span className="text-[11px] uppercase text-slate-500 font-semibold">Additional notes</span>
            <textarea data-testid="imaging-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40" />
          </label>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Cancel</button>
          <button data-testid="imaging-submit-btn" onClick={submit} disabled={busy} className="h-10 px-5 rounded-lg bg-[#1976D2] text-white text-sm font-semibold hover:bg-[#1565C0] disabled:opacity-50">
            {busy ? "Sending…" : "Send to radiology"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Also export for reuse from Doctor.jsx and PatientChart.jsx
export { NewOrderModal as ImagingOrderModal };